import * as React from 'react';
import { InjectedTranslatedProps, translate } from 'react-i18next';
import classNames from 'classnames';
import { Text, TYPOGRAPHY } from 'wix-ui-tpa/Text';
import { Input } from 'wix-ui-tpa/Input';
import { Button, PRIORITY } from 'wix-ui-tpa/Button';
import { Dropdown } from 'wix-ui-core/dropdown';
import { Option } from 'wix-ui-core/dropdown-option';
import styles from './CreateGroupModal.st.css';
import { ApiTypes } from '@wix/social-groups-api';

// debugger;
const SOCIAL_GROUP_NAME_MAX_LENGTH = 50;
const DEFAULT_SOCIAL_GROUP_TYPE = ApiTypes.v1.GroupPrivacyLevel.PUBLIC;

interface SocialGroupTypeDetails {
  name: string;
  description: string;
}

export interface CreateGroupModalProps {
  onSubmit(
    groupDetails: ApiTypes.v1.GroupDetails,
    settings: ApiTypes.v1.GroupSettings,
  ): void;
  onRequestClose(): void;
}

interface CreateGroupState {
  title: string;
  privacyLevel: ApiTypes.v1.GroupPrivacyLevel;
}

export class CreateGroupModal extends React.Component<
  CreateGroupModalProps & InjectedTranslatedProps,
  CreateGroupState
> {
  state = {
    title: '',
    privacyLevel: DEFAULT_SOCIAL_GROUP_TYPE,
  };

  handleOnSubmit = e => {
    e.preventDefault();
    const { title, privacyLevel } = this.state;
    if (this.isValid()) {
      this.props.onSubmit({ title }, { privacyLevel });
    }
  };

  isValid() {
    const { title } = this.state;
    return !!title;
  }

  setSocialGroupName = e => {
    this.setState({ title: e.target.value });
  };

  setSocialGroupType = option => {
    this.setState({ privacyLevel: option.id });
  };

  renderGroupTypeOption = (
    details: SocialGroupTypeDetails,
    id: ApiTypes.v1.GroupPrivacyLevel,
  ) => {
    return (
      <div data-hook={`option-${id}`}>
        <div>{details.name}</div>
        <div>{details.description}</div>
      </div>
    );
  };

  getGroupTypeOptions = (): Option[] => {
    return [
      ApiTypes.v1.GroupPrivacyLevel.PUBLIC,
      ApiTypes.v1.GroupPrivacyLevel.PRIVATE,
      ApiTypes.v1.GroupPrivacyLevel.SECRET,
    ].map(type => {
      const details = this.getGroupTypeDetails(type);
      return {
        id: type,
        isDisabled: false,
        isSelectable: true,
        render: this.renderGroupTypeOption.bind(null, details, type),
        value: details.name,
      };
    });
  };

  getGroupTypeDetails(
    type: ApiTypes.v1.GroupPrivacyLevel,
  ): SocialGroupTypeDetails {
    const { t } = this.props;
    switch (type) {
      case ApiTypes.v1.GroupPrivacyLevel.PRIVATE:
        return {
          name: t('groups-web.types.private.name'),
          description: t('groups-web.types.private.description'),
        };
      case ApiTypes.v1.GroupPrivacyLevel.PUBLIC:
        return {
          name: t('groups-web.types.public.name'),
          description: t('groups-web.types.public.description'),
        };
      case ApiTypes.v1.GroupPrivacyLevel.SECRET:
        return {
          name: t('groups-web.types.secret.name'),
          description: t('groups-web.types.secret.description'),
        };
      default:
        return null;
    }
  }

  render() {
    const { t, onRequestClose } = this.props;
    const { title, privacyLevel } = this.state;
    const selectedGroupDetails = this.getGroupTypeDetails(privacyLevel);

    return (
      <form
        {...styles('root', {}, this.props)}
        data-hook="create-group-root"
        onSubmit={this.handleOnSubmit}
      >
        <div className={classNames(styles.control, styles.title)}>
          <Text typography={TYPOGRAPHY.largeTitle} tagName="h2">
            {t('groups-web.modals.create-group.title')}
          </Text>
        </div>

        <div className={styles.control}>
          <Text>
            <div className={styles.label}>
              <span>{t('groups-web.modals.create-groups.groupName')}</span>
              <span>
                {title.length}/{SOCIAL_GROUP_NAME_MAX_LENGTH}
              </span>
            </div>
          </Text>
          <Input
            data-hook="group-name"
            maxLength={SOCIAL_GROUP_NAME_MAX_LENGTH}
            placeholder={t(
              'groups-web.modals.create-groups.groupName.placeholder',
            )}
            onChange={this.setSocialGroupName}
          />
        </div>

        <div className={styles.control}>
          <Text>
            <span>{t('groups-web.modals.create-groups.groupType')}</span>
          </Text>
          <Dropdown
            data-hook="group-type"
            style={{ display: 'block' }}
            onDeselect={this.setSocialGroupType}
            onSelect={this.setSocialGroupType}
            openTrigger="click"
            options={this.getGroupTypeOptions()}
            placement="auto"
            initialSelectedIds={[DEFAULT_SOCIAL_GROUP_TYPE]}
            onInitialSelectedOptionsSet={null}
          >
            <Input
              readOnly
              value={selectedGroupDetails ? selectedGroupDetails.name : ''}
            />
          </Dropdown>
          <Text>
            {selectedGroupDetails ? selectedGroupDetails.description : ''}
          </Text>
        </div>

        <div className={classNames(styles.control, styles.buttons)}>
          <Button
            data-hook="cancel-button"
            priority={PRIORITY.secondary}
            className={styles.cancelButton}
            onClick={onRequestClose}
          >
            {t('groups-web.btn.cancel')}
          </Button>
          <Button
            data-hook="create-button"
            priority={PRIORITY.primary}
            className={styles.createButton}
            type="submit"
          >
            {t('groups-web.modals.create-group.title')}
          </Button>
        </div>
      </form>
    );
  }
}

// TODO: find better way
export default translate()(CreateGroupModal) as React.ComponentType<
  CreateGroupModalProps
>;
